// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Normalises markdown / emoji-laden assistant output for SpeechSynthesis.
 *
 * Why this exists:
 *   - macOS / Chrome / Edge TTS engines pronounce emojis literally
 *     ("smiling face", "rocket"). LLM output is full of them.
 *   - Markdown markers (**, _, `) get spelled out in some engines and
 *     interrupt prosody in others.
 *   - List items rendered as inline text without terminating punctuation
 *     cause the engine to accelerate ("speed up on lists") and run them
 *     together without a breath. Period-terminated sentences fix both.
 *
 * The output is text-only; no SSML (Web Speech API doesn't reliably
 * support it across browsers).
 */
export function prepareTextForSpeech(input: string): string {
  if (!input) return '';

  let s = input;

  // 1. Strip emoji + pictographs + regional indicators + variation selectors
  s = s.replace(/\p{Extended_Pictographic}/gu, ' ');
  s = s.replace(/[\u{1F1E6}-\u{1F1FF}]/gu, ' '); // flag regional indicators
  s = s.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');  // skin-tone modifiers
  // ZWJ (U+200D) + variation selectors (U+FE0E text, U+FE0F emoji).
  // Three separate single-char replaces so lint doesn't flag a
  // misleading character class on the invisible combined forms.
  s = s.split('‍').join('');
  s = s.split('︎').join('');
  s = s.split('️').join('');

  // 2. Strip code blocks first (they often contain symbols TTS shouldn't speak)
  s = s.replace(/```[\s\S]*?```/g, ' ');

  // 3. Inline markdown formatting — keep the inner text, drop markers
  s = s.replace(/\*\*([^*]+?)\*\*/g, '$1');  // bold **
  s = s.replace(/__([^_]+?)__/g, '$1');       // bold __
  s = s.replace(/\*([^*\n]+?)\*/g, '$1');     // italic *
  s = s.replace(/(?<!\w)_([^_\n]+?)_(?!\w)/g, '$1'); // italic _
  s = s.replace(/`([^`\n]+?)`/g, '$1');       // inline code

  // 4. Headings — strip the leading hashes; keep the heading text
  s = s.replace(/^\s*#{1,6}\s+/gm, '');

  // 5. Images and links — drop image tags entirely; keep link labels
  s = s.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');

  // 6. Blockquote markers
  s = s.replace(/^\s*>\s?/gm, '');

  // 7. List markers — strip the marker, leave the item text. Items get
  //    sentence terminators added in step 9 so the engine pauses naturally.
  s = s.replace(/^\s*[-*+]\s+/gm, '');
  s = s.replace(/^\s*\d+[.)]\s+/gm, '');

  // 8. Horizontal rules
  s = s.replace(/^[-_*]{3,}$/gm, '');

  // 9. Per-line sentence terminator. A trailing space-period gives most
  //    TTS engines a clean breath without sounding like a hard stop.
  s = s
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      if (/[.!?:;,]$/.test(trimmed)) return trimmed;
      return trimmed + '.';
    })
    .filter(Boolean)
    .join(' ');

  // 10. Collapse runs of whitespace introduced by emoji-stripping
  s = s.replace(/\s+/g, ' ').trim();

  return s;
}
